import sdk, { client } from "@/sdk";

import {
  BalanceHistoryEntry,
  DetailedWithdrawRequestRow,
  RefundHistoryEntry,
  UserBankInfo,
} from "./types";

const REFUND_BALANCE_QUERY = /* GraphQL */ `
  query refundGetBalanceByUserId($_id: ID!) {
    getBalanceByUserId(_id: $_id) {
      _id
      user {
        _id
        phoneNo
        chosenPortfolio {
          _id
          index
        }
      }
      fee
      netAssetValue
      cashAmount
      holdAmount
      totalDeposit
      bonds {
        bond {
          name
          symbol
        }
        principleAmount
        accumulatedAmount
        estimatedTotalEarnings
        fee
        history {
          _id
          principleAmount
          fee
          createdAt
        }
      }
      stocks {
        stock {
          name
          symbol
        }
        totalAmount
        totalPrice
        unitPrice
        fee
        history {
          usdRate
          holdAmount
          totalAmount
          totalPrice
          unitPrice
          fee
          createdAt
        }
      }
      savings {
        saving {
          bankName
          accountNo
        }
        principleAmount
        estimatedTotalEarnings
        accumulatedAmount
        fee
        history {
          _id
          principleAmount
          fee
          createdAt
        }
      }
      balanceHistory {
        _id
        title
        cashAmount
        holdAmount
        principleAmount
        fee
        createdAt
        stocks {
          stock {
            name
            symbol
          }
          totalAmount
          totalPrice
          holdAmount
          unitPrice
          fee
        }
        bonds {
          bond {
            symbol
            name
          }
          principleAmount
          fee
        }
        savings {
          saving {
            bankName
            accountNo
          }
          principleAmount
          fee
        }
        previousPurchases {
          stocks {
            stock {
              name
              symbol
            }
            totalAmount
            totalPrice
            holdAmount
            unitPrice
            fee
          }
          createdAt
        }
      }
    }
  }
`;

type RefundBalanceResponse = {
  getBalanceByUserId?: {
    user?: {
      _id?: string | null;
      phoneNo?: string | null;
      chosenPortfolio?: {
        _id?: string | null;
        index?: number | null;
      } | null;
    } | null;
    cashAmount?: number | null;
    holdAmount?: number | null;
    balanceHistory?: Array<Record<string, unknown>> | null;
  } | null;
};

export interface LookupResult {
  bankInfoMap: Record<string, UserBankInfo>;
  balanceHistoryMap: Record<string, BalanceHistoryEntry[]>;
  refundHistoryMap: Record<string, RefundHistoryEntry[]>;
}

type CustomerLookup = {
  email: string;
  regNumber: string;
  phone: string;
  lastName: string;
  firstName: string;
};

type UserWithdrawalHistoryResponse = {
  userWithdrawalRequests?: Array<{
    status?: string | null;
    createdAt?: number | string | null;
    bonds?: Array<{
      amount?: number | null;
      purchasedAt?: string | null;
      bond?: {
        name?: string | null;
      } | null;
    }> | null;
    stocks?: Array<{
      quantity?: number | null;
      purchasedAt?: string | null;
      stock?: {
        symbol?: string | null;
      } | null;
    }> | null;
  }>;
};

const getRefundBalanceByUserId = async (
  userId: string,
): Promise<RefundBalanceResponse> => {
  return client.request<RefundBalanceResponse>(REFUND_BALANCE_QUERY, {
    _id: userId,
  });
};

const normalizeLookupPhone = (phone: string): string =>
  String(phone || "").replace(/\D/g, "");

const buildLookupTerms = (
  regNumber: string,
  email: string,
  phone: string,
  lastName?: string,
  firstName?: string,
): string[] => {
  const terms: string[] = [];
  const seen = new Set<string>();
  const push = (value?: string) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    terms.push(trimmed);
  };

  const normalizedPhone = normalizeLookupPhone(phone);
  const emailLocalPart = String(email || "").split("@")[0]?.trim() || "";
  const baseLocalPart = emailLocalPart.split(".")[0]?.trim() || "";
  const fullName = [lastName, firstName]
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(" ");

  push(regNumber);
  push(email);
  push(emailLocalPart);
  push(baseLocalPart);
  push(normalizedPhone);
  if (normalizedPhone.startsWith("976") && normalizedPhone.length > 8) {
    push(normalizedPhone.slice(3));
  }
  if (normalizedPhone.length === 8) {
    push(`+976${normalizedPhone}`);
    push(`976${normalizedPhone}`);
  }
  push(lastName);
  push(firstName);
  push(fullName);

  return terms;
};

const searchAndMatch = async (
  term: string,
  email: string,
  phone?: string,
): Promise<{ user?: Record<string, unknown>; wrongMatch?: string }> => {
  const result = await sdk.getUsers({
    page: 0,
    limit: 5,
    advanced: { search: term },
  });
  const users = (result.getUsers?.users || []) as Record<string, unknown>[];
  const normalizedEmail = String(email || "").toLowerCase().trim();
  const normalizedPhone = normalizeLookupPhone(phone || "");
  const matched = users.find(
    (user) =>
      String(user.email || "").toLowerCase().trim() === normalizedEmail ||
      (normalizedPhone !== "" &&
        normalizeLookupPhone(String(user.phoneNo || "")) === normalizedPhone),
  );
  if (matched) return { user: matched };
  if (users.length > 0) {
    return { wrongMatch: users.map((user) => user.email).join(", ") };
  }
  return {};
};

const searchAcrossTerms = async (
  terms: string[],
  email: string,
  phone: string,
): Promise<{ user?: Record<string, unknown>; wrongMatch?: string }> => {
  let lastWrongMatch: string | undefined;
  for (const term of terms) {
    const result = await searchAndMatch(term, email, phone);
    if (result.user) return result;
    if (result.wrongMatch) lastWrongMatch = result.wrongMatch;
  }
  return lastWrongMatch ? { wrongMatch: lastWrongMatch } : {};
};

const fetchRefundHistoryForUser = async (
  userId: string,
): Promise<RefundHistoryEntry[]> => {
  const response = await (
    sdk as unknown as {
      userWithdrawalRequests: (args: {
        userId: string;
      }) => Promise<UserWithdrawalHistoryResponse>;
    }
  ).userWithdrawalRequests({ userId });

  return (response.userWithdrawalRequests || [])
    .map((request) => ({
      createdAt: Number(request.createdAt) || 0,
      status: String(request.status || ""),
      bondCount: Array.isArray(request.bonds) ? request.bonds.length : 0,
      stocks:
        ((request.stocks || []) as NonNullable<
          UserWithdrawalHistoryResponse["userWithdrawalRequests"]
        >[number]["stocks"])
          ?.map((stock) => ({
            symbol: String(stock?.stock?.symbol || "").trim().toUpperCase(),
            quantity: Number(stock?.quantity) || 0,
            purchasedAt: String(stock?.purchasedAt || ""),
          }))
          .filter(
            (stock) => stock.symbol && stock.quantity > 0 && stock.purchasedAt,
          ) || [],
      bonds:
        ((request.bonds || []) as NonNullable<
          UserWithdrawalHistoryResponse["userWithdrawalRequests"]
        >[number]["bonds"])
          ?.map((bond) => ({
            name: String(bond?.bond?.name || "").trim(),
            amount: Number(bond?.amount) || 0,
            purchasedAt: String(bond?.purchasedAt || ""),
          }))
          .filter((bond) => bond.name && bond.amount > 0 && bond.purchasedAt) ||
        [],
    }))
    .sort((a, b) => a.createdAt - b.createdAt);
};

const mapBalanceHistoryEntry = (
  entry: Record<string, unknown>,
): BalanceHistoryEntry => ({
  _id: String(entry._id || ""),
  title: String(entry.title || ""),
  cashAmount: Number(entry.cashAmount) || 0,
  holdAmount: Number(entry.holdAmount) || 0,
  principleAmount: Number(entry.principleAmount) || 0,
  fee: Number(entry.fee) || 0,
  createdAt: String(entry.createdAt || ""),
  stocks: ((entry.stocks as Array<Record<string, unknown>>) || []).map(
    (stock) => ({
      stock: stock.stock as { name: string; symbol: string } | undefined,
      totalAmount: Number(stock.totalAmount) || 0,
      totalPrice: Number(stock.totalPrice) || 0,
      holdAmount: Number(stock.holdAmount) || 0,
      unitPrice: Number(stock.unitPrice) || 0,
      fee: Number(stock.fee) || 0,
    }),
  ),
  bonds: ((entry.bonds as Array<Record<string, unknown>>) || []).map(
    (bond) => ({
      bond: bond.bond as { symbol: string; name: string } | undefined,
      principleAmount: Number(bond.principleAmount) || 0,
      fee: Number(bond.fee) || 0,
    }),
  ),
});

const dedupCustomersByEmail = (
  rows: DetailedWithdrawRequestRow[],
): CustomerLookup[] => {
  return [
    ...new Map(
      rows.map((row) => [
        row.email.toLowerCase().trim(),
        {
          email: row.email.toLowerCase().trim(),
          regNumber: row.regNumber.trim(),
          phone: row.phone?.trim() || "",
          lastName: row.lastName?.trim() || "",
          firstName: row.firstName?.trim() || "",
        },
      ]),
    ).values(),
  ];
};

export const fetchCustomerDataByEmail = async (
  detailedWithdrawData: DetailedWithdrawRequestRow[],
): Promise<{
  files: Record<
    string,
    {
      purchaseData: Record<string, unknown>[];
      txHistoryData: Record<string, unknown>[];
    }
  >;
  successCount: number;
  totalCount: number;
  skipped: string[];
}> => {
  const allCustomers = dedupCustomersByEmail(detailedWithdrawData);
  const files: Record<
    string,
    {
      purchaseData: Record<string, unknown>[];
      txHistoryData: Record<string, unknown>[];
    }
  > = {};
  let successCount = 0;
  const skipped: string[] = [];
  const retryByPhone: CustomerLookup[] = [];

  const fetchAndStoreByEmail = async (
    user: Record<string, unknown>,
    email: string,
    regNumber: string,
  ) => {
    const balanceResult = await getRefundBalanceByUserId(user._id as string);
    const balanceHistory =
      balanceResult.getBalanceByUserId?.balanceHistory || [];
    const data = {
      purchaseData: balanceHistory.map((entry: Record<string, unknown>) => ({
        ...entry,
        _source: "balanceHistory",
      })),
      txHistoryData: [],
    };
    files[email] = data;
    if (!files[regNumber]) files[regNumber] = data;
  };

  for (const customer of allCustomers) {
    const { email, regNumber, phone, lastName, firstName } = customer;
    try {
      const { user, wrongMatch } = await searchAcrossTerms(
        buildLookupTerms(regNumber, email, phone, lastName, firstName),
        email,
        phone,
      );
      if (user) {
        console.log(`[fetch-email] ✓ ${email} found by regNumber`);
        await fetchAndStoreByEmail(user, email, regNumber);
        successCount++;
      } else if (wrongMatch) {
        console.warn(
          `[fetch-email] regNumber "${regNumber}" → ${wrongMatch} (not ${email}), will retry by phone`,
        );
        retryByPhone.push(customer);
      } else {
        files[email] = { purchaseData: [], txHistoryData: [] };
        skipped.push(`${regNumber} (${email}): олдсонгүй`);
        successCount++;
      }
    } catch (error) {
      files[email] = { purchaseData: [], txHistoryData: [] };
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      skipped.push(`${regNumber} (${email}): ${errorMessage}`);
      successCount++;
    }
  }

  for (const customer of retryByPhone) {
    const { email, regNumber, phone, lastName, firstName } = customer;
    if (!phone) {
      files[email] = { purchaseData: [], txHistoryData: [] };
      skipped.push(`${regNumber} (${email}): олдсонгүй (утас байхгүй)`);
      successCount++;
      continue;
    }
    try {
      const { user } = await searchAcrossTerms(
        buildLookupTerms(regNumber, email, phone, lastName, firstName),
        email,
        phone,
      );
      if (user) {
        console.log(`[fetch-email] ✓ ${email} found by phone "${phone}"`);
        await fetchAndStoreByEmail(user, email, regNumber);
        successCount++;
      } else {
        files[email] = { purchaseData: [], txHistoryData: [] };
        skipped.push(`${regNumber} (${email}): утсаар ч олдсонгүй`);
        successCount++;
      }
    } catch (error) {
      files[email] = { purchaseData: [], txHistoryData: [] };
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      skipped.push(`${regNumber} (${email}): ${errorMessage}`);
      successCount++;
    }
  }

  return { files, successCount, totalCount: allCustomers.length, skipped };
};

export const lookupUserBankInfoByEmail = async (
  rows: DetailedWithdrawRequestRow[],
): Promise<LookupResult> => {
  const bankInfoMap: Record<string, UserBankInfo> = {};
  const balanceHistoryMap: Record<string, BalanceHistoryEntry[]> = {};
  const refundHistoryMap: Record<string, RefundHistoryEntry[]> = {};

  const customers = dedupCustomersByEmail(rows);
  let foundCount = 0;
  const retryByPhone: CustomerLookup[] = [];

  const fetchAndStoreUser = async (
    user: Record<string, unknown>,
    email: string,
  ) => {
    const userId = String(user._id);
    const userDetail = await sdk.getUser({ _id: userId });
    if (!userDetail.getUser) return;

    let cashTotal = 0;
    let balanceHoldAmount = 0;
    let portfolioIndex = 0;
    try {
      const balanceResult = await getRefundBalanceByUserId(userId);
      cashTotal = balanceResult.getBalanceByUserId?.cashAmount || 0;
      balanceHoldAmount = balanceResult.getBalanceByUserId?.holdAmount || 0;
      portfolioIndex =
        balanceResult.getBalanceByUserId?.user?.chosenPortfolio?.index || 0;

      const balanceHistory =
        balanceResult.getBalanceByUserId?.balanceHistory || [];
      if (balanceHistory.length > 0) {
        balanceHistoryMap[email] = balanceHistory.map(mapBalanceHistoryEntry);
      }
    } catch (balanceError) {
      console.warn(`Failed to fetch balance for ${email}:`, balanceError);
    }

    try {
      refundHistoryMap[email] = await fetchRefundHistoryForUser(userId);
    } catch (refundError) {
      console.warn(`Failed to fetch refund history for ${email}:`, refundError);
      refundHistoryMap[email] = [];
    }

    bankInfoMap[email] = {
      bankName: userDetail.getUser.bankInfo?.bankName || "",
      accountNo: userDetail.getUser.bankInfo?.accountNo || "",
      depositCode: userDetail.getUser.depositCode || "",
      lastName: userDetail.getUser.lastName || "",
      firstName: userDetail.getUser.firstName || "",
      cashTotal,
      holdAmount: balanceHoldAmount,
      portfolioIndex,
    };
    foundCount++;
  };

  for (const customer of customers) {
    const { email, regNumber, phone, lastName, firstName } = customer;
    if (!regNumber) continue;
    try {
      const { user, wrongMatch } = await searchAcrossTerms(
        buildLookupTerms(regNumber, email, phone, lastName, firstName),
        email,
        phone,
      );
      if (user) {
        console.log(`[lookup-email] ✓ ${email} found by regNumber`);
        await fetchAndStoreUser(user, email);
        continue;
      }

      const byEmail = await searchAcrossTerms(
        buildLookupTerms(regNumber, email, phone, lastName, firstName),
        email,
        phone,
      );
      if (byEmail.user) {
        console.log(`[lookup-email] ✓ ${email} found by email fallback`);
        await fetchAndStoreUser(byEmail.user, email);
      } else if (wrongMatch) {
        console.warn(
          `[lookup-email] regNumber "${regNumber}" → ${wrongMatch} (not ${email}), will retry by phone`,
        );
        retryByPhone.push(customer);
      } else {
        console.warn(`[lookup-email] ${regNumber} (${email}): олдсонгүй`);
      }
    } catch (error) {
      console.warn(`Failed to lookup user: ${regNumber} (${email})`, error);
    }
  }

  for (const customer of retryByPhone) {
    const { email, regNumber, phone, lastName, firstName } = customer;
    if (!phone) {
      console.warn(
        `[lookup-email] ${regNumber} (${email}): олдсонгүй (утас байхгүй)`,
      );
      continue;
    }
    try {
      const { user } = await searchAcrossTerms(
        buildLookupTerms(regNumber, email, phone, lastName, firstName),
        email,
        phone,
      );
      if (user) {
        console.log(`[lookup-email] ✓ ${email} found by phone "${phone}"`);
        await fetchAndStoreUser(user, email);
      } else {
        const byEmail = await searchAcrossTerms(
          buildLookupTerms(regNumber, email, phone, lastName, firstName),
          email,
          phone,
        );
        if (byEmail.user) {
          console.log(
            `[lookup-email] ✓ ${email} found by email fallback after phone retry`,
          );
          await fetchAndStoreUser(byEmail.user, email);
        } else {
          console.warn(
            `[lookup-email] ${regNumber} (${email}): утсаар болон email-р ч олдсонгүй`,
          );
        }
      }
    } catch (error) {
      console.warn(
        `Failed to lookup user by phone: ${regNumber} (${email})`,
        error,
      );
    }
  }

  console.log(
    `Lookup complete: ${foundCount} found out of ${customers.length}`,
  );
  return { bankInfoMap, balanceHistoryMap, refundHistoryMap };
};
